/**
 * ConversationSidebar — Left panel of Ask Argus
 *
 * Demo-port design (R2): clean ChatGPT-style list with date group
 * headers + pinned section + ellipsis menu. No virtualizer, no
 * filter chips, no multi-select bulk action bar. These were
 * Session 3 additions that the founder did not ask for and that
 * coupled the sidebar tightly to layout-edge cases.
 *
 * Wiring is REAL — bound to live conversations API:
 *   - GET    /conversations            (useInfiniteQuery, paginated)
 *   - PATCH  /conversations/:id        (pin/unpin via inline action)
 *   - DELETE / PATCH / POST            (modal actions in conversation-modals.tsx)
 *
 * Mutations invalidate `['conversations']` so the list stays fresh.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Plus, Search, Pencil, Trash2, Archive,
  Share2, Download, Copy, Bookmark, FolderOpen,
  Star, MoreVertical, ChevronRight, Loader2, Brain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ConversationModals, type ModalType } from './conversation-modals';
import {
  type Conversation,
  groupConversationsByDate,
} from '@/lib/chat-types';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

const PAGE_SIZE = 50;

// ── Backend wire types ─────────────────────────────────────────────────────

interface ConversationDTO {
  id: string;
  title: string;
  status: string;
  pinned: boolean;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  agentId?: string | null;
  projectId?: string | null;
}

interface ConversationsPageResponse {
  data: ConversationDTO[];
  total: number;
  page: number;
  pageSize: number;
}

function dtoToConversation(dto: ConversationDTO): Conversation {
  return {
    id: dto.id,
    title: dto.title || 'Untitled',
    pinned: dto.pinned,
    updatedAt: new Date(dto.updatedAt),
    messageCount: dto.messageCount,
    agentId: dto.agentId ?? undefined,
    projectId: dto.projectId ?? undefined,
  };
}

// ── Component ──────────────────────────────────────────────────────────────

interface ConversationSidebarProps {
  activeId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
}

export function ConversationSidebar({
  activeId,
  onSelect,
  onNewChat,
}: ConversationSidebarProps) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  // Debounced copy driving the SERVER-side search. Typing filters the loaded
  // pages instantly (below); this makes the backend search the whole history a
  // beat later, so a conversation from last month is findable at all - the
  // filter alone only ever saw the pages already fetched.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [modalConversation, setModalConversation] = useState<{ id: string; title: string }>({
    id: '',
    title: '',
  });

  const openModal = useCallback((modal: ModalType, conv: Conversation) => {
    setModalConversation({ id: conv.id, title: conv.title });
    setActiveModal(modal);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  // Real backend query — paginated conversations list.
  const convQuery = useInfiniteQuery({
    // The term is part of the key so each search gets its own paginated
    // result set. Existing invalidateQueries({ queryKey: ['conversations'] })
    // calls still match this by prefix.
    queryKey: ['conversations', debouncedSearch],
    initialPageParam: 1 as number,
    queryFn: async ({ pageParam }) => {
      const page = pageParam as number;
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      return apiClient.get<ConversationsPageResponse>(`/conversations?${params}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (fetched >= lastPage.total) return undefined;
      return allPages.length + 1;
    },
  });

  const conversations = useMemo<Conversation[]>(
    () =>
      (convQuery.data?.pages ?? [])
        .flatMap((p) => p.data)
        .map(dtoToConversation),
    [convQuery.data],
  );

  // bug #4 — resolve project associations so each conversation row can
  // show a project badge. Shares the ['ai-projects'] cache with the
  // projects page (no extra request when that's already loaded).
  const projectsQuery = useQuery<{
    projects: Array<{ id: string; name: string; color: string }>;
  }>({
    queryKey: ['ai-projects'],
    queryFn: () =>
      apiClient.get<{ projects: Array<{ id: string; name: string; color: string }> }>(
        '/ai-projects',
      ),
    staleTime: 60_000,
  });
  const projectsById = useMemo(() => {
    const m = new Map<string, { name: string; color: string }>();
    for (const p of projectsQuery.data?.projects ?? []) {
      m.set(p.id, { name: p.name, color: p.color });
    }
    return m;
  }, [projectsQuery.data]);

  // Instant local narrowing while the debounced server search catches up. The
  // server has already filtered by `debouncedSearch`; this only sharpens the
  // result for characters typed since, so it never hides a server match.
  const filteredConvs = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => c.title.toLowerCase().includes(q));
  }, [conversations, search]);

  const pinned = useMemo(() => filteredConvs.filter((c) => c.pinned), [filteredConvs]);
  const grouped = useMemo(() => groupConversationsByDate(filteredConvs), [filteredConvs]);

  // Pin/unpin mutation — wired directly from the ellipsis menu.
  const pinMutation = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      apiClient.patch(`/conversations/${id}`, { pinned }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error('Failed to update pin status');
    },
  });

  const isLoading = convQuery.isLoading;
  const isError = convQuery.isError;
  const isEmpty = !isLoading && !isError && filteredConvs.length === 0;

  return (
    <div className="flex flex-col h-full" data-testid="conversation-sidebar">
      {/* Header */}
      <div className="p-3 space-y-2 shrink-0">
        <Button onClick={onNewChat} className="w-full justify-center gap-2" size="sm">
          <Plus className="h-4 w-4" /> New Chat
        </Button>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search chats..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm bg-muted/30 border-border/30"
            data-testid="conversation-search"
          />
        </div>
      </div>

      {/* Projects link */}
      <Link
        to="/ask-argus/projects"
        className="mx-3 mb-2 flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-muted/50 transition-colors shrink-0"
      >
        <FolderOpen className="h-4 w-4 text-muted-foreground" />
        <span className="flex-1 text-sm">Projects</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>

      {/* Memories link — what Argus remembers about you (ASK-M1 D-notice) */}
      <Link
        to="/ask-argus/memories"
        data-testid="memories-link"
        className="mx-3 mb-2 flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-muted/50 transition-colors shrink-0"
      >
        <Brain className="h-4 w-4 text-muted-foreground" />
        <span className="flex-1 text-sm">Memories</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>

      <Separator className="mx-3 mb-1" />

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
        {isLoading && (
          <div
            className="flex items-center justify-center py-6 gap-2 text-xs text-muted-foreground"
            data-testid="conversation-list-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {isError && !isLoading && (
          <div
            className="flex flex-col items-center justify-center py-6 px-3 text-xs text-destructive text-center"
            data-testid="conversation-list-error"
          >
            <p>Failed to load conversations.</p>
            <button
              className="mt-1 underline"
              onClick={() => void convQuery.refetch()}
            >
              Retry
            </button>
          </div>
        )}

        {isEmpty && !isLoading && !isError && (
          <div
            className="flex items-center justify-center py-6 px-3 text-xs text-muted-foreground text-center"
            data-testid="conversation-list-empty"
          >
            {search.trim()
              ? 'No conversations match your search.'
              : 'No conversations yet. Start a new chat!'}
          </div>
        )}

        {!isLoading && !isError && !isEmpty && (
          <div className="px-2 pb-3">
            {pinned.length > 0 && (
              <>
                <GroupHeader label="Pinned" />
                {pinned.map((conv) => (
                  <ChatRow
                    key={conv.id}
                    conversation={conv}
                    project={conv.projectId ? projectsById.get(conv.projectId) : undefined}
                    isActive={conv.id === activeId}
                    onSelect={onSelect}
                    onOpenModal={openModal}
                    onTogglePin={(c) =>
                      pinMutation.mutate({ id: c.id, pinned: !c.pinned })
                    }
                  />
                ))}
              </>
            )}

            {Object.entries(grouped).map(([group, convs]) => (
              <div key={group}>
                <GroupHeader label={group} />
                {convs.map((conv) => (
                  <ChatRow
                    key={conv.id}
                    conversation={conv}
                    project={conv.projectId ? projectsById.get(conv.projectId) : undefined}
                    isActive={conv.id === activeId}
                    onSelect={onSelect}
                    onOpenModal={openModal}
                    onTogglePin={(c) =>
                      pinMutation.mutate({ id: c.id, pinned: !c.pinned })
                    }
                  />
                ))}
              </div>
            ))}

            {convQuery.hasNextPage && (
              <div className="flex justify-center py-2">
                <button
                  onClick={() => void convQuery.fetchNextPage()}
                  disabled={convQuery.isFetchingNextPage}
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {convQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals — already wired to backend */}
      <ConversationModals
        modal={activeModal}
        onClose={closeModal}
        conversationTitle={modalConversation.title}
        conversationId={modalConversation.id}
      />
    </div>
  );
}

// ── Group Header ─────────────────────────────────────────────────────────

function GroupHeader({ label }: { label: string }) {
  return (
    <p className="text-[11px] font-medium text-muted-foreground px-2 pt-4 pb-1">
      {label}
    </p>
  );
}

// ── Chat Row ─────────────────────────────────────────────────────────────

function ChatRow({
  conversation,
  project,
  isActive,
  onSelect,
  onOpenModal,
  onTogglePin,
}: {
  conversation: Conversation;
  project?: { name: string; color: string };
  isActive: boolean;
  onSelect: (id: string) => void;
  onOpenModal: (modal: ModalType, conv: Conversation) => void;
  onTogglePin: (conv: Conversation) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      data-testid="conversation-row"
      className={cn(
        'group rounded-lg cursor-pointer transition-colors px-2 py-1.5',
        isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50 text-foreground',
      )}
      onClick={() => onSelect(conversation.id)}
    >
      <div className="flex items-center gap-1">
        <p className="flex-1 min-w-0 truncate text-[13px]">{conversation.title}</p>

        <span className="text-[10px] text-foreground/40 shrink-0 tabular-nums">
          {formatRelativeTime(conversation.updatedAt)}
        </span>

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center justify-center w-6 h-6 rounded-md bg-foreground/10 text-foreground hover:bg-foreground/20 shrink-0 transition-colors"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              aria-label={`Actions for ${conversation.title}`}
              title={`Actions for ${conversation.title}`}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} className="w-48 z-50">
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('share', conversation)}>
              <Share2 className="h-4 w-4" /> Share
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('download', conversation)}>
              <Download className="h-4 w-4" /> Download
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('rename', conversation)}>
              <Pencil className="h-4 w-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2"
              onClick={() => onTogglePin(conversation)}
            >
              <Bookmark className="h-4 w-4" />
              {conversation.pinned ? 'Unpin' : 'Pin'}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('clone', conversation)}>
              <Copy className="h-4 w-4" /> Clone
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('add-project', conversation)}>
              <Star className="h-4 w-4" /> Add to Project
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => onOpenModal('archive', conversation)}>
              <Archive className="h-4 w-4" /> Archive
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 text-destructive"
              onClick={() => onOpenModal('delete', conversation)}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* bug #4 — project association badge. Shown when the conversation is
          attached to a project (folder_id) so the main list reflects the
          project membership, not just the project's own detail page. */}
      {project && (
        <div
          className="mt-0.5 flex items-center gap-1 min-w-0"
          title={`Project: ${project.name}`}
        >
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: project.color }}
          />
          <span className="truncate text-[10px] text-foreground/50">
            {project.name}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}
